import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { maxlength, minlength, regex, required } from 'validation-rules'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import { ConfirmationDialog } from 'functional-foundations'
import { Typography } from 'foundations-library/components'
import StepTitle from '../../StepTitle'
import useFlatForm from '../../../hooks/useFlatForm'
import { getOtherUseCaseTextValue, useCasesOptions, replacePlaceholderWithActualBrandValues } from '../../../utils/util'
import TextareaWithTitles from '../../TextareaWithTitles'
import useDisableInput from '../../../hooks/useDisableInput'

const MIN_MESSAGE_LENGTH = 20
const MAX_MESSAGE_LENGTH = 1024

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 30
    },
    mb5: {
        marginBottom: 5
    }
}))

const numberToWord = {
    1: 'One',
    2: 'Two',
    3: 'Three',
    4: 'Four',
    5: 'Five'
}

const textValues = JSON.parse(getValue('sms_enrollment_campaigns_2nd_step'))

const generateUseCaseSampleMessage = (useCases: string[], brand) => {
    const sampleMessages = []
    const otherValue = getOtherUseCaseTextValue(useCases)
    for (const useCase of useCases) {
        if (useCase === otherValue) { // other
            sampleMessages.push(replacePlaceholderWithActualBrandValues(textValues.OTHER_sample_message, brand))
        } else if (useCasesOptions.some((x) => x.id === useCase && x.sampleMessage)) {
            const message = (useCasesOptions.find((x) => x.id === useCase && x.sampleMessage)).sampleMessage
            sampleMessages.push(replacePlaceholderWithActualBrandValues(message, brand))
        } else {
            sampleMessages.push('')
        }
    }

    return sampleMessages
}

/**
 * @param {object} props
 */
const SampleMessages = (props) => {
    const smsContentData = props.passData.campaign_sms_content
    const brand = props.passData?.campaign_usage?.selectedBrand
    const useCases = props.passData?.campaign_usage?.useCases || []
    const useCaseSampleMessage = generateUseCaseSampleMessage(useCases, brand)

    const { editing } = props
    const { disableAllInputs } = useDisableInput({ mode: props.mode, item: props.editing })

    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp,
        isFormDataValid
    } = useFlatForm({
        helpMessage:
            smsContentData?.helpMessage ||
            (editing != null ? editing.keywords.help.CampaignkeywordValue : '') ||
            replacePlaceholderWithActualBrandValues(textValues.help_response_value, brand),
        optInMessage:
            smsContentData?.optInMessage ||
            (editing != null ? editing.keywords.start.CampaignkeywordValue : '') ||
            replacePlaceholderWithActualBrandValues(textValues.first_message_opted_in_value, brand),
        optOutMessage:
            smsContentData?.optOutMessage ||
            (editing != null ? editing.keywords.stop.CampaignkeywordValue : '') ||
            replacePlaceholderWithActualBrandValues(textValues.stop_response_value, brand),
        messageOne:
            smsContentData?.messageOne ||
            (editing != null ? editing.samplesms1 : useCaseSampleMessage[0]),
        messageTwo:
            smsContentData?.messageTwo ||
            (editing != null ? editing.samplesms2 : useCaseSampleMessage[1]),
        messageThree:
            smsContentData?.messageThree ||
            (editing != null ? editing.samplesms3 : useCaseSampleMessage[2]),
        messageFour:
            smsContentData?.messageFour ||
            (editing != null ? editing.samplesms4 : useCaseSampleMessage[3]),
        messageFive:
            smsContentData?.messageFive ||
            (editing != null ? editing.samplesms5 : useCaseSampleMessage[4])
    }, {
        messageOne: !useCaseSampleMessage[0],
        messageTwo: !useCaseSampleMessage[1],
        messageThree: !useCaseSampleMessage[2],
        messageFour: !useCaseSampleMessage[3],
        messageFive: !useCaseSampleMessage[4]
    })

    const classes = useStyles()
    const [showConfirmModal, setShowConfirmModal] = useState(false)

    const gotoNextStep = () => {
        setShowConfirmModal(false)
        props.update?.({ completed: true, gotoNext: true, saveData: data })
    }

    const onRejectHandler = () => {
        setShowConfirmModal(false)
    }

    useEffect(() => {
        if (props.saveId) {
            if (props.gotoStepIndex > props.stepIndex) {
                setShowConfirmModal(true)
            } else {
                gotoNextStep()
            }
        }
    }, [props.saveId, props.gotoStepIndex, props.stepIndex])

    useEffect(() => {
        props.update?.({ disableNextButton: () => !isFormDataValid })
    }, [isFormDataValid])

    return <div>
        <StepTitle remoteConfigID='sms_enrollment_sample_message' />
        <div className={classes.container}>
            <div>
                <Typography variant='subtitle1' className={classes.mb5}>
                    General messages
                </Typography>
                <Typography variant='body1'>
                    These pre-populated examples fulfill industry requirements. You may leave them as-is or edit them as you wish, but remember to include all information in <strong>bold</strong>.
                </Typography>
            </div>
            <TextareaWithTitles
                value={data.helpMessage}
                onChange={setDataProp('helpMessage')}
                onClear={onClear('helpMessage')}
                isValid={setIsValidProp('helpMessage')}
                rules={[
                    required(),
                    regex(new RegExp('STOP'), undefined, 'Your message has to include word \'STOP\''),
                    minlength(MIN_MESSAGE_LENGTH),
                    maxlength(MAX_MESSAGE_LENGTH)
                ]}
                title={textValues.help_response_title}
                subtitle={textValues.help_response_subtitle}
                placeholder={textValues.first_message_opted_in_placeholder}
                minHeight={94}
                disabled={disableAllInputs}
            />
            <TextareaWithTitles
                value={data.optInMessage}
                onChange={setDataProp('optInMessage')}
                onClear={onClear('optInMessage')}
                isValid={setIsValidProp('optInMessage')}
                rules={[
                    required(),
                    regex(new RegExp('STOP'), undefined, 'Your message has to include word \'STOP\''),
                    regex(new RegExp('HELP'), undefined, 'Your message has to include word \'HELP\''),
                    minlength(MIN_MESSAGE_LENGTH),
                    maxlength(MAX_MESSAGE_LENGTH)
                ]}
                title={textValues.first_message_opted_in}
                subtitle={textValues.first_message_opted_in_subtitle}
                placeholder={textValues.help_response_placeholder}
                disabled={disableAllInputs}
            />
            <TextareaWithTitles
                value={data.optOutMessage}
                onChange={setDataProp('optOutMessage')}
                onClear={onClear('optOutMessage')}
                isValid={setIsValidProp('optOutMessage')}
                rules={[
                    required(),
                    regex(new RegExp('START'), undefined, 'Your message has to include word \'START\''),
                    minlength(MIN_MESSAGE_LENGTH),
                    maxlength(MAX_MESSAGE_LENGTH)
                ]}
                title={textValues.stop_response_title}
                subtitle={textValues.stop_response_subtitle}
                placeholder={textValues.stop_response_placeholder}
                disabled={disableAllInputs}
            />
            <div>
                <Typography variant='subtitle1' className={classes.mb5}>
                    Campaign messages
                </Typography>
                <Typography variant='body2'>
                    These should be specific to your business/use case and accurately reflect the messages that you’ll be sending. They must also include the business name, “STOP” and “HELP” language. See examples at the bottom of the page for reference.
                </Typography>
            </div>
            <div className={classes.container}>
                {
                    useCaseSampleMessage.map((useCase, i) => {
                        return <TextareaWithTitles
                            value={data[`message${numberToWord[i + 1]}`]}
                            onChange={setDataProp(`message${numberToWord[i + 1]}`)}
                            onClear={onClear(`message${numberToWord[i + 1]}`)}
                            isValid={setIsValidProp(`message${numberToWord[i + 1]}`)}
                            rules={[required(), minlength(MIN_MESSAGE_LENGTH), maxlength(MAX_MESSAGE_LENGTH)]}
                            title={`${textValues.sample_message_title} (${i + 1} of ${useCaseSampleMessage.length})`}
                            subtitle={textValues.sample_message_subtitle}
                            placeholder={textValues.sample_message_placeholder}
                            key={`sample-message-${i}`}
                            disabled={disableAllInputs}
                        />
                    })
                }
            </div>
        </div>
        <ConfirmationDialog
            width={600}
            title='Confirmation'
            isOpen={showConfirmModal}
            onConfirm={gotoNextStep}
            onCancel={onRejectHandler}
            confirmText='Confirm and continue'
            cancelText='Edit'
            cancelProps={{ variant: 'outlined' }}
            confirmProps={{ icon: 'go' }}

        >
            <Typography
                remoteConfigID='sms_enrollment_please_confirm_campaign_registration'
                variant='body1'
                align='center'
            />
        </ConfirmationDialog>
    </div>
}

SampleMessages.propTypes = {
    passData: PropTypes.object,
    update: PropTypes.func,
    saveId: PropTypes.number,
    gotoStepIndex: PropTypes.number,
    stepIndex: PropTypes.number,
    editing: PropTypes.object,
    showTitle: PropTypes.bool,
    showContainer: PropTypes.bool,
    mode: PropTypes.string
}

export default SampleMessages
